import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, MapPin, Users, Trophy, Star, Heart, Download, FileText, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeetYouthAthletesPage = () => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchAthletes();
  }, []);

  const fetchAthletes = async (retryCount = 0) => {
    try {
      setError(false);
      if (retryCount > 0) setRetrying(true);
      
      console.log('Fetching youth athletes from:', `${API_URL}/api/youth-athletes`);
      const response = await axios.get(`${API_URL}/api/youth-athletes`, { timeout: 15000 });
      console.log('Youth athletes response:', response.data);
      
      const athleteList = response.data?.athletes || [];
      setAthletes(athleteList);
    } catch (err) {
      console.error('Failed to fetch youth athletes:', err);
      console.error('Error details:', err.response?.data || err.message);
      
      if (retryCount < 3) {
        console.log(`Retrying... attempt ${retryCount + 1}/3`);
        setTimeout(() => fetchAthletes(retryCount + 1), 1500);
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight uppercase text-foreground leading-none mb-6">
            Youth Athletes
          </h1>
          <p className="font-serif text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
            Meet the next generation of champions. These young ropers are working hard, competing at the highest levels, and inspiring others to chase their dreams.
          </p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Star className="w-5 h-5 text-primary" />
              <span>Rising Stars</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Trophy className="w-5 h-5 text-secondary" />
              <span>Future Champions</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Heart className="w-5 h-5 text-accent" />
              <span>Passion & Dedication</span>
            </div>
          </div>
        </div>
      </section>

      {/* Athletes Grid */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-muted-foreground">
                {retrying ? 'Retrying connection...' : 'Loading athletes...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl uppercase mb-2">Unable to Load Athletes</h3>
              <p className="text-muted-foreground mb-4">Please check your connection and try again.</p>
              <Button onClick={() => { setLoading(true); fetchAthletes(); }} variant="outline">
                Try Again
              </Button>
            </div>
          ) : athletes.length === 0 ? (
            <div className="text-center py-12">
              <Star className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl uppercase mb-2">Youth Athletes Coming Soon</h3>
              <p className="text-muted-foreground">We're building our roster of rising stars. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {athletes.map(athlete => (
                <Card 
                  key={athlete.id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1" 
                  data-testid={`athlete-card-${athlete.id}`}
                  onClick={() => navigate(`/youth-athletes/${athlete.id}`)}
                >
                  <div className="aspect-square bg-gradient-to-br from-primary/20 to-secondary/20 relative">
                    {athlete.photo_url ? (
                      <img 
                        src={athlete.photo_url} 
                        alt={athlete.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: athlete.photo_position?.objectPosition || 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-display font-bold text-8xl text-primary/30">
                          {athlete.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {athlete.region && (
                      <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-primary" />
                        <span className="text-xs font-medium">{athlete.region}</span>
                      </div>
                    )}
                    {athlete.age && (
                      <div className="absolute top-4 right-4 bg-primary/90 text-primary-foreground rounded-full px-3 py-1">
                        <span className="text-xs font-bold">{athlete.age} years old</span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">
                      {athlete.name}
                    </h3>
                    {athlete.disciplines?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {athlete.disciplines.map(d => (
                          <Badge key={d} variant="secondary" className="text-xs">
                            {d}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {athlete.achievements?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {athlete.achievements.slice(0, 3).map((achievement, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-700">
                            <Trophy className="w-3 h-3 mr-1" />
                            {achievement}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {athlete.bio && (
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                        {athlete.bio}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Youth Sponsorship Draw Application */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-2 border-primary/20 rounded-2xl overflow-hidden">
            <div className="bg-primary/5 px-6 sm:px-8 py-8 sm:py-10">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 rounded-full p-3 flex-shrink-0">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight uppercase text-foreground mb-3">
                    Youth Sponsorship Draw Application
                  </h2>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    Elevated Roping Media is proud to offer sponsorship opportunities for youth athletes aged 17 and under who are actively competing on the rodeo or jackpot trail. Selected athletes will receive sponsor apparel, media coverage, and platform promotion throughout the season.
                  </p>
                  <div className="space-y-2 text-sm text-muted-foreground mb-6">
                    <p className="flex items-center gap-2"><Trophy className="w-4 h-4 text-secondary flex-shrink-0" /> Open to athletes 17 years and under</p>
                    <p className="flex items-center gap-2"><Star className="w-4 h-4 text-secondary flex-shrink-0" /> Must be an active Elevated Roping Media subscriber (or family member)</p>
                    <p className="flex items-center gap-2"><Heart className="w-4 h-4 text-secondary flex-shrink-0" /> Committed to competing in a minimum of 12 events this season</p>
                  </div>
                  <div className="flex justify-center sm:justify-start">
                    <Link to="/youth-application">
                      <Button 
                        size="lg"
                        className="rounded-full px-8 py-6 font-bold uppercase tracking-wider"
                        data-testid="apply-youth-sponsorship-btn"
                      >
                        <Send className="w-5 h-5 mr-2" />
                        Apply Online
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase mb-4">
            Support Our Youth
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            These young athletes represent the future of our sport. Join our community to support and follow their journeys.
          </p>
          <Link to="/signup">
            <Button 
              size="lg" 
              variant="secondary"
              className="rounded-full px-10 py-6 font-bold uppercase tracking-wider"
              data-testid="join-community-btn"
            >
              Join Our Community
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-4">
            Ready to Get Coached?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Sign up today and learn from championship-level coaches.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 font-bold uppercase tracking-wider"
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/coaches">
              <Button 
                variant="outline"
                size="lg" 
                className="rounded-full px-10 py-6 font-bold uppercase tracking-wide border-2"
              >
                Meet Our Coaches
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MeetYouthAthletesPage;
